import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Button, TextField, Autocomplete, Select, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import Progress from "../../../components/progress/Progress";
import "../../../assets/css/time.css";
import BuildingService from "../../../services/building/BuildingService";
import IEditBuildingRequest from "../../../models/requests/building/editBuildingRequest";
import ISchedule from "../../../models/Schedule";
import moment from "moment";
import { routes } from "../../../routes";
import {
    FormWrapper,
    EntryWrapper,
    EntryTitle,
    EntryField,
} from "../../../components/customStyles/form";
import ITimeZoneInfo from "../../../models/timezone";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import { IPropertyGroup } from "../../../models/propertyGroup";
import EditOnly from "../../../components/permissions/EditOnly";

export const defaultMinTime = moment()
    .set({ hours: 0, minutes: 0, seconds: 0, millisecond: 0 })
    .toDate();
export const defaultMaxTime = moment()
    .set({ hours: 23, minutes: 59, seconds: 59, millisecond: 999 })
    .toDate();

const ticksInMilisecond = 10000;
const convertToTicks = (baseTime: Date, targetTime: Date) =>
    (targetTime.getTime() - baseTime.getTime()) * ticksInMilisecond;

export const convertScheduleToTicks = (targetTime: Date) => ({
    monday: convertToTicks(defaultMinTime, targetTime),
    tuesday: convertToTicks(defaultMinTime, targetTime),
    wednesday: convertToTicks(defaultMinTime, targetTime),
    thursday: convertToTicks(defaultMinTime, targetTime),
    friday: convertToTicks(defaultMinTime, targetTime),
    saturday: convertToTicks(defaultMinTime, targetTime),
    sunday: convertToTicks(defaultMinTime, targetTime),
});

const BuildingAddPage = () => {
    const { t } = useTranslation(["common", "admin"]);

    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [description, setDescription] = useState("");

    const [timeZones, setTimeZones] = useState<ITimeZoneInfo[]>([]);
    const timeZoneOptions = timeZones.map((timeZone, index) => ({ label: timeZone.displayName, id: timeZone.id }));
    const [selectedTZOption, setSelectedTZOption] = useState<{ label: string, id: string }>({ label: "", id: "" });

    const [isFormSending, setIsFormSending] = useState(false);
    const [propertyGroupId, setPropertyGroupId] = useState(0);
    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);

    const onCreateBuilding = () => {
        setIsFormSending(true);

        const openingSchedule: ISchedule =
            convertScheduleToTicks(defaultMinTime);
        const closingSchedule: ISchedule =
            convertScheduleToTicks(defaultMaxTime);

        const building: IEditBuildingRequest = {
            id: 0,
            name,
            address,
            openingSchedule,
            closingSchedule,
            timezone: selectedTZOption.id,
            propertyGroupId

        };

        BuildingService.PostBuilding(building)
            .then(() => navigate(routes.tableRoute.buildings))
            .catch(console.error)
            .finally(() => setIsFormSending(false));
    };

    useEffect(() => {
        Promise.all([BuildingService.GetTimeZones(), BuildingService.GetDefaultTimeZone()]).then(([timeZones, defaultTZ]) => {
            if (!timeZones) return;
            setTimeZones(timeZones)
            const index = timeZones.findIndex((timeZone) => timeZone.id === defaultTZ)
            if (index < 0) return;
            setSelectedTZOption({ label: timeZones[index].displayName, id: timeZones[index].id })
        });
    }, [])

    useEffect(() => {
        propertyGroupService.GetPropertyGroups().then(setPropertyGroups);
    }, [])
    return (
        <Box component={Paper} sx={{ width: "100%" }}>
            <FormWrapper>
                <EntryWrapper>
                    <EntryTitle>{t("table.building.name")} *</EntryTitle>
                    <EntryField>
                        <TextField
                            required={true}
                            style={{ textAlign: "left", width: "100%" }}
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                            }}
                        />
                    </EntryField>
                </EntryWrapper>
                <EntryWrapper>
                    <EntryTitle>{t("table.building.address")} *</EntryTitle>
                    <EntryField>
                        <TextField
                            style={{ textAlign: "left", width: "100%" }}
                            value={address}
                            onChange={(e) => {
                                setAddress(e.target.value);
                            }}
                        />
                    </EntryField>
                </EntryWrapper>
                <EntryWrapper>
                    <EntryTitle>
                        {t("table.building.description")}
                    </EntryTitle>
                    <EntryField>
                        <TextField
                            style={{ textAlign: "left", width: "100%" }}
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                            }}
                        />
                    </EntryField>
                </EntryWrapper>

                <EntryWrapper>
                    <EntryTitle>{t("table.building.timezone")} * </EntryTitle>
                    <EntryField>

                        <Autocomplete
                            value={selectedTZOption}
                            onChange={(e, newValue) => setSelectedTZOption(newValue || { label: "", id: "" })}
                            disablePortal
                            sx={{ width: "100%", maxWidth: "100%", height: "2em" }}
                            options={timeZoneOptions}
                            isOptionEqualToValue={(option, value) => option.id === value.id || option.label === value.label}
                            renderInput={(params) => (
                                <TextField
                                    required={true}
                                    {...params}
                                    InputLabelProps={{
                                        children: null,
                                    }}
                                    onKeyDown={(e) => { e.key === "Enter" && e.preventDefault(); }}
                                />
                            )}
                        />
                    </EntryField>
                </EntryWrapper>


                <EntryWrapper>
                    <EntryTitle>{t("table.property.propertyGroup")} *</EntryTitle>
                    <EntryField>
                        <Select
                            notched
                            size="small"
                            fullWidth
                            defaultValue={-1}
                            value={propertyGroupId}
                            onChange={(event) =>
                                setPropertyGroupId(Number(event.target.value))
                            }
                            id="property-select"
                        >
                            {propertyGroups.map((propertyGroup) => (
                                <MenuItem
                                    key={`property_${propertyGroup.id}`}
                                    value={propertyGroup.id}
                                >
                                    {propertyGroup.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </EntryField>
                </EntryWrapper>

                <EntryWrapper sx={{ justifyContent: "flex-end" }}>
                    <Progress isLoading={isFormSending}>
                        <EditOnly>
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                disabled={!name.trim() || !address.trim() || !selectedTZOption.id.trim() || !propertyGroupId}
                                onClick={onCreateBuilding}
                                sx={{ margin: "1em", marginLeft: "0" }}
                            >
                                {t("table.building.create")}
                            </Button>
                        </EditOnly>
                    </Progress>
                </EntryWrapper>
            </FormWrapper>
        </Box>
    );
};

export default BuildingAddPage;
